import { Copy } from 'lucide-react'
import { type ExternalToast, toast } from 'sonner'
import ColoredNumber from '@/components/ColoredNumber'
import { Logger } from '@/components/Logger'
import { NotificationItemComponent } from '@/components/notifications/NotificationItemComponent'
import { Tag } from '@/components/orders/Tag'
import { OrderSide, TradeTypes } from '@/constants/trade'
import {
  getUserSelfQueryKey,
  orderKind,
  orderModifierKind,
  orderStatus,
  User,
  UserRole,
} from '@/hooks/v1'
import type { Asset } from '@/hooks/v1/types/Asset'
import type { GetUserSelfStatus200 } from '@/hooks/v1/types/GetUserSelf'
import type { Order } from '@/hooks/v1/types/Order'
import type { OrderBook } from '@/hooks/v1/types/OrderBook'
import { queryClient } from '@/services/api'
import { store } from '@/store'
import { addNotification, updatePartialNotification } from '@/store/notificationSlice'
import { countDecimalPlaces, formatNumber } from '@/utils/numberUtils'
import { playSound } from './sounds'

export type NotificationItem = {
  id: string
  title: string
  params: Inexpressible
  notificationStatus: NotificationStatus
  notificationType: NotificationType
  timestamp: number
}

export enum NotificationStatus {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

export enum NotificationType {
  ADD_LIQUIDITY = 'addLiquidity',
  DEPOSIT_WITHDRAW_BALANCE = 'addRemoveBalance',
  ASSET_OPERATIONS = 'assetOperations',
  CREATE_ASSET = 'createAsset',
  CREATE_ORDER = 'createOrder',
  CREATE_ORDER_BOOK = 'createOrderBook',
  DISPOSABLE = 'disposable',
  ORDER_CANCEL = 'orderCancel',
  ORDER_EXECUTED_OR_CANCELED = 'orderExecutedOrCanceled',
  ORDER_PARTIAL = 'orderPartial',
  ORDER_LIQUIDATION = 'orderLiquidation',
  REMOVE_LIQUIDITY = 'removeLiquidity',
  ROLES = 'roles',
  SIMPLE = 'simple',
  TOKEN = 'token',
  REPAY = 'repay',
  POSITION_CLOSED = 'positionClosed',
}

export const LIQUIDATION_ALERT_EVENT = 'liquidation-alert'

const NotificationId = ({ value, displayValue }: { value: string; displayValue: string }) => (
  <span className="inline-flex min-w-0 items-center gap-1.5">
    <span className="min-w-0 font-mono font-semibold text-primary">{displayValue}</span>
    <button
      type="button"
      aria-label={`Copy order ID ${value}`}
      className="shrink-0 text-white/55 transition-colors hover:text-white"
      onClick={(event) => {
        event.stopPropagation()
        navigator.clipboard.writeText(value).finally(() => {
          createNotification(
            'Copied to clipboard',
            `Order ID ${value} has been copied to clipboard`,
            NotificationType.DISPOSABLE,
          )
        })
      }}
    >
      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  </span>
)

export type LiquidationAlertDetail = {
  accountId: string
  orderBookId: string
}

const areToastsEnabled = () => {
  const userSelf = queryClient.getQueryData<GetUserSelfStatus200>(getUserSelfQueryKey())
  if (!userSelf) {
    return true
  }
  return userSelf?.data?.notifications_enabled === true
}

const orderNotificationTypes = new Set<NotificationType>([
  NotificationType.CREATE_ORDER,
  NotificationType.ORDER_CANCEL,
  NotificationType.ORDER_EXECUTED_OR_CANCELED,
  NotificationType.ORDER_PARTIAL,
])

const areOrderNotificationsEnabled = () => {
  const userSelf = queryClient.getQueryData<GetUserSelfStatus200>(getUserSelfQueryKey())
  if (!userSelf) {
    return true
  }
  return userSelf?.data?.allow_orders_notifications === true
}

function areLiquidationNotificationsEnabled() {
  const userSelf = queryClient.getQueryData<GetUserSelfStatus200>(getUserSelfQueryKey())
  if (!userSelf) return true

  return userSelf.data?.allow_liquidations_notifications === true
}

const shouldSuppressOrderNotification = (
  notificationType: NotificationType,
  notificationStatus: NotificationStatus,
) =>
  orderNotificationTypes.has(notificationType) &&
  notificationStatus !== NotificationStatus.ERROR &&
  !areOrderNotificationsEnabled()

export function triggerLiquidationAlert(order: Order) {
  if (typeof window === 'undefined') return

  const accountId = order.position_id?.trim()
  const orderBookId = order.order_book_id?.trim()
  if (!accountId || !orderBookId) return

  window.dispatchEvent(
    new CustomEvent<LiquidationAlertDetail>(LIQUIDATION_ALERT_EVENT, {
      detail: { accountId, orderBookId },
    }),
  )
}

export const generateTokenNotification = (token: string) => {
  return (
    <div className={'flex flex-col gap-1'}>
      <div className="text-sm">Token copied to clipboard</div>
      <div className="text-sm flex flex-row gap-1">
        <div>Token:</div>
        <div className={'text-primary'}>{token}</div>
      </div>
    </div>
  )
}

export const generateRolesNotification = (roles: UserRole[], user: User) => {
  return (
    <div className={'flex flex-col gap-1'}>
      <div className={'text-sm flex flex-row gap-1'}>
        <div>Role updated to:</div>
        <div className={'text-primary'}>{roles[roles.length - 1]}</div>
      </div>

      <div className={'text-sm flex flex-row gap-1'}>
        <div>User:</div>
        <div className={'text-primary'}>{user.email}</div>
      </div>
    </div>
  )
}

export const generateAddRemoveBalanceNotification = (
  selectedAsset: Asset,
  text: string,
  amount: string,
  isAdding: boolean,
) => {
  const displayAmount = Number.isNaN(Number(amount)) ? 0 : Number(amount)
  return (
    <div className={'flex flex-col'}>
      <div className={'text-sm'}>{text}</div>
      <div className={'text-sm flex flex-row gap-1 mt-2'}>
        <div>Amount:</div>
        <div className={isAdding ? 'text-success' : 'text-danger'}>
          <ColoredNumber
            value={displayAmount}
            decimals={selectedAsset?.decimals ?? 0}
            integerClassName="border-0 text-start text-white font-semibold"
          />
        </div>
      </div>
      <div className={'text-sm flex flex-row gap-1'}>
        <div>Asset:</div>
        <div className={'text-primary'}>{selectedAsset?.symbol}</div>
      </div>
    </div>
  )
}

export const generateAssetOperationsNotification = (
  text: string,
  amount: string,
  asset: Asset | undefined,
) => {
  const displayAmount = Number.isNaN(Number(amount)) ? 0 : Number(amount)

  return (
    <div className={'flex flex-col'}>
      <div className={'text-sm'}>{text}</div>
      <div className={'text-sm flex flex-row gap-1 mt-2'}>
        <div>Amount:</div>
        <div className={'text-success'}>
          <ColoredNumber
            value={displayAmount}
            decimals={asset?.decimals ?? 0}
            integerClassName="border-0 text-start text-white font-semibold"
          />
        </div>
      </div>
      <div className={'text-sm flex flex-row gap-1'}>
        <div>Asset:</div>
        <div className={'text-primary'}>{asset?.symbol}</div>
      </div>
    </div>
  )
}

export const generateCreateAssetNotification = (symbol: string) => {
  return (
    <div className={'flex flex-col'}>
      <div className={'text-sm'}>Asset created successfully</div>
      <div className={'text-sm flex flex-row gap-1 mt-2'}>
        <div>Asset:</div>
        <div className={'text-primary'}>{symbol}</div>
      </div>
    </div>
  )
}

export const generateCreateOrderBookNotification = (baseAsset: string, quoteAsset: string) => {
  return (
    <div className={'flex flex-col'}>
      <div className={'text-sm'}>Pool created successfully</div>
      <div className={'text-sm flex flex-row gap-1 mt-2'}>
        <div>Base Asset:</div>
        <div className={'text-primary'}>{baseAsset}</div>
      </div>

      <div className={'text-sm flex flex-row gap-1 mt-2'}>
        <div>Quote Asset:</div>
        <div className={'text-primary'}>{quoteAsset}</div>
      </div>
    </div>
  )
}

export const generateOrderCancelNotification = (orderID: string) => {
  const slicedOrderID = `${orderID.slice(0, 8)}...${orderID.slice(-8)}`
  return (
    <div className={'flex flex-col'}>
      <div className={'text-sm'}>Order cancellation requested</div>
      <div className={'text-sm flex flex-row gap-1'}>
        <div>ID:</div>
        <NotificationId value={orderID} displayValue={slicedOrderID} />
      </div>
    </div>
  )
}

export const generateAddLiquidityNotification = (
  orderBooks: OrderBook[],
  allAssets: Asset[],
  orderbookId: string,
  baseAssetShares: string,
  quoteAssetShares: string,
  poolShares: string,
) => {
  const orderBook = orderBooks.find((orderBook) => orderBook.order_book_id === orderbookId)
  const poolAsset = allAssets.find((asset) => asset?.id === orderBook?.shares_asset_id)
  const baseAsset = allAssets.find((asset) => asset?.id === orderBook?.base_asset_id)
  const quoteAsset = allAssets.find((asset) => asset?.id === orderBook?.quote_asset_id)

  return (
    <div className={'space-y-2 text-sm'}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">Pool</div>
        <div className={'font-semibold text-white'}>
          {baseAsset?.symbol} / {quoteAsset?.symbol}
        </div>
      </div>

      <div className={'space-y-1'}>
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">Supplied</div>
        <div key={`${orderbookId}base-asset`} className="flex items-center gap-2">
          <div className={'text-primary/90'}>•</div>
          <div className={'text-primary'}>
            <ColoredNumber
              value={baseAssetShares}
              decimals={baseAsset?.decimals ?? 0}
              integerClassName="border-0 text-start text-white font-semibold"
            />{' '}
            {baseAsset?.symbol}
          </div>
        </div>
        <div key={`${orderbookId}quote-asset`} className="flex items-center gap-2">
          <div className={'text-primary/90'}>•</div>
          <div className={'text-primary'}>
            <ColoredNumber
              value={quoteAssetShares}
              decimals={quoteAsset?.decimals ?? 0}
              integerClassName="border-0 text-start text-white font-semibold"
            />{' '}
            {quoteAsset?.symbol}
          </div>
        </div>
      </div>
      <div
        key={`${orderbookId}shares`}
        className="flex items-center justify-between border-t border-white/10 pt-2"
      >
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">LP Tokens</div>
        <div className={'font-semibold text-primary'}>
          <ColoredNumber
            value={poolShares}
            decimals={poolAsset?.decimals ?? 0}
            integerClassName="border-0 text-start text-white font-semibold"
          />
        </div>
      </div>
    </div>
  )
}

export const generateRemoveLiquidityNotification = (
  orderBooks: OrderBook[],
  allAssets: Asset[],
  orderbookId: string,
  baseAssetShares: string,
  quoteAssetShares: string,
  shares: string,
) => {
  const orderBook = orderBooks.find((orderBook) => orderBook.order_book_id === orderbookId)

  const poolAsset = allAssets.find((asset) => asset?.id === orderBook?.shares_asset_id)
  const baseAsset = allAssets.find((asset) => asset?.id === orderBook?.base_asset_id)
  const quoteAsset = allAssets.find((asset) => asset?.id === orderBook?.quote_asset_id)

  return (
    <div className={'space-y-2 text-sm'}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">Pool</div>
        <div className={'font-semibold text-white'}>
          {baseAsset?.symbol} / {quoteAsset?.symbol}
        </div>
      </div>

      <div className={'space-y-1'}>
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">Received</div>
        <div key={`${orderbookId}base-asset`} className="flex items-center gap-2">
          <div className={'text-primary/90'}>•</div>
          <div className={'text-primary'}>
            <ColoredNumber
              value={baseAssetShares}
              decimals={baseAsset?.decimals ?? 0}
              integerClassName="border-0 text-start text-white font-semibold"
            />{' '}
            {baseAsset?.symbol}
          </div>
        </div>
        <div key={`${orderbookId}quote-asset`} className="flex items-center gap-2">
          <div className={'text-primary/90'}>•</div>
          <div className={'text-primary'}>
            <ColoredNumber
              value={quoteAssetShares}
              decimals={quoteAsset?.decimals ?? 0}
              integerClassName="border-0 text-start text-white font-semibold"
            />{' '}
            {quoteAsset?.symbol}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-white/10 pt-2">
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">
          LP Tokens removed
        </div>
        <div className={'font-semibold text-primary'}>
          <ColoredNumber
            value={shares}
            decimals={poolAsset?.decimals ?? 0}
            integerClassName="border-0 text-start text-white font-semibold"
          />
        </div>
      </div>
    </div>
  )
}

export const generateRepayNotification = (asset: Asset, amount: string) => {
  return (
    <div className={'flex flex-col'}>
      <div className={'text-sm'}>Repay successfully</div>
      <div className={'text-sm flex flex-row gap-1 mt-2'}>
        <div>Asset:</div>
        <div className={'text-primary'}>{asset?.symbol}</div>
      </div>
      <div className={'text-sm flex flex-row gap-1'}>
        <div>Amount:</div>
        <div>
          {Number.isNaN(Number(amount)) ? (
            'Invalid amount'
          ) : (
            <ColoredNumber
              value={Number(amount)}
              decimals={asset?.decimals ?? 0}
              integerClassName="border-0 text-start text-white font-semibold"
            />
          )}
        </div>
      </div>
    </div>
  )
}

const generatePositionClosedNotification = (
  realizedPnlValue: number | string | undefined,
  accountValue: boolean | string | undefined,
  assetValue: string | undefined,
) => {
  const realizedPnl = Number(realizedPnlValue)
  const safeRealizedPnl = Number.isFinite(realizedPnl) ? realizedPnl : 0
  const accountText =
    typeof accountValue === 'boolean'
      ? accountValue
        ? 'Global'
        : 'Isolated'
      : accountValue?.toLowerCase() === 'global'
        ? 'Global'
        : 'Isolated'
  const assetText = assetValue?.trim() ? assetValue : '--'

  const pnlClass =
    safeRealizedPnl > 0 ? 'text-success' : safeRealizedPnl < 0 ? 'text-danger' : 'text-white-dark'
  const pnlPrefix = safeRealizedPnl > 0 ? '+$' : safeRealizedPnl < 0 ? '-$' : '$'
  const realizedPnlText = `${pnlPrefix}${formatNumber(Math.abs(safeRealizedPnl), 3)}`

  return (
    <div className={'space-y-1 text-sm'}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-white/70">Account:</div>
        <div className="font-semibold text-white">{accountText}</div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="text-white/70">Asset:</div>
        <div className="font-semibold text-white">{assetText}</div>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="text-white/70">Realized P&L:</div>
        <div className={`font-semibold ${pnlClass}`}>{realizedPnlText}</div>
      </div>
    </div>
  )
}

//"Pool Name" "Order Type" submitted for "Amt" "Base Asset" at Limit "Limit Px" "Quote Asset"
const generateNewOrderNotification = (
  orderID: string,
  selectedTradeType: TradeTypes,
  selectedOrderSide: OrderSide,
  priceValue: string,
  leverageValue: string,
  baseAsset: Asset,
  quoteAsset: Asset,
  size: string,
  tickSize: number,
) => {
  const slicedOrderID = `${orderID.slice(0, 8)}...${orderID.slice(-8)}`
  const selectedTradeTypeString = selectedTradeType === TradeTypes.LIMIT ? 'Limit' : 'Market'
  const leverage = Number(leverageValue)

  return (
    <div className={'space-y-2 text-sm'}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">ID</div>
        <NotificationId value={orderID} displayValue={slicedOrderID} />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">Pool</div>
        <div className={'font-semibold text-white'}>
          {baseAsset?.symbol} / {quoteAsset?.symbol}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">Order Type</div>
        <div
          className={`font-semibold ${selectedOrderSide === OrderSide.BUY ? 'text-success' : 'text-danger'}`}
        >
          {selectedTradeType} {selectedOrderSide}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">Submitted</div>
        <div className="flex items-center gap-2">
          <div className={'text-primary/90'}>•</div>
          <div className={'text-primary'}>
            <ColoredNumber
              value={size}
              decimals={baseAsset?.decimals ?? 0}
              integerClassName="border-0 text-start text-white font-semibold"
            />{' '}
            {baseAsset?.symbol}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">
          At {selectedTradeTypeString}
        </div>
        <div className={'font-semibold text-primary'}>
          <ColoredNumber
            value={priceValue}
            decimals={countDecimalPlaces(tickSize)}
            integerClassName="border-0 text-start text-white font-semibold"
          />{' '}
          {quoteAsset?.symbol}
        </div>
      </div>

      {leverage > 1 && (
        <div className="flex items-center justify-between border-t border-white/10 pt-2">
          <div className="text-[11px] uppercase tracking-[0.08em] text-white/55">Leverage</div>
          <div className={'font-semibold text-primary'}>
            <ColoredNumber
              value={leverage}
              decimals={0}
              integerClassName="border-0 text-start text-white font-semibold"
              suffix="x"
            />
          </div>
        </div>
      )}
    </div>
  )
}

const generateSimpleNotification = (params: Inexpressible) => {
  if (typeof params === 'string') {
    return <div>{params}</div>
  }

  return <div>{JSON.stringify(params)}</div>
}

const generateOrderLiquidation = (order: Order, baseAsset?: Asset) => {
  const { side } = order
  const isBuy = side === OrderSide.BUY

  const decimalsOut = baseAsset?.decimals ?? 0
  const amount = order?.original_quantity
  const amountOutNumber = Number.isNaN(Number(amount)) ? 0 : Number(amount)
  const amountOut = formatNumber(amountOutNumber, decimalsOut)
  const slicedOrderID = order?.order_id
    ? `${order?.order_id.slice(0, 8)}...${order?.order_id.slice(-8)}`
    : ''
  const orderTypeString = order?.kind === orderKind.LIMIT ? 'Limit' : 'Market'
  const hasAmount = amount !== undefined

  return (
    <div className={'flex flex-col gap-1'}>
      <div className={'text-sm font-semibold text-danger'}>Position liquidated</div>
      <div className={'text-sm flex flex-row gap-1'}>
        <div>ID:</div>
        <NotificationId value={order.order_id} displayValue={slicedOrderID} />
      </div>
      <div className={'flex flex-row gap-1'}>
        <div>Order Type:</div>
        <div className={isBuy ? 'text-success' : 'text-danger'}>{orderTypeString}</div>
      </div>
      {hasAmount && (
        <div className={'flex flex-row gap-1'}>
          <div>Submitted for</div>
          <div className={'text-white'}>
            <ColoredNumber
              value={amountOut}
              decimals={decimalsOut}
              integerClassName="border-0 text-start text-white font-semibold"
            />
          </div>
          {baseAsset?.symbol && <Tag>{baseAsset?.symbol}</Tag>}
        </div>
      )}

      <div className={'text-sm flex flex-row gap-1'}>
        <div>Modifier:</div>
        <div>{orderModifierKind.LIQUIDATION}</div>
      </div>
    </div>
  )
}

const generateOrderStateChanged = (order: Order) => {
  const orderStateText = order?.status === orderStatus.FILLED ? 'Order Executed' : 'Order Cancelled'
  const slicedOrderID = order?.order_id
    ? `${order?.order_id.slice(0, 8)}...${order?.order_id.slice(-8)}`
    : ''
  const isLiquidationOrderModifier = order?.order_modifiers?.includes(orderModifierKind.LIQUIDATION)

  return (
    <div className={'flex flex-col gap-1'}>
      <div className={'text-sm'}>{orderStateText}</div>
      <div className={'text-sm flex flex-row gap-1'}>
        <div>ID:</div>
        <NotificationId value={order.order_id} displayValue={slicedOrderID} />
      </div>
      {isLiquidationOrderModifier && (
        <div className={'text-sm flex flex-row gap-1'}>
          <div>Modifier:</div>
          <div>{orderModifierKind.LIQUIDATION}</div>
        </div>
      )}
    </div>
  )
}

const generateOrderStatePartial = (order: Order, baseAsset: Asset, quoteAsset: Asset) => {
  const selectedOrderSide = order?.side
  const slicedOrderID = order?.order_id
    ? `${order?.order_id.slice(0, 8)}...${order?.order_id.slice(-8)}`
    : ''

  const decimals = selectedOrderSide === OrderSide.BUY ? baseAsset?.decimals : quoteAsset?.decimals
  const filled = (
    <ColoredNumber
      value={Number(order?.filled_quantity ?? 0)}
      decimals={decimals ?? 0}
      integerClassName="border-0 text-start text-white font-semibold"
    />
  )
  const total = (
    <ColoredNumber
      value={Number(order?.open_quantity ?? 0)}
      decimals={decimals ?? 0}
      integerClassName="border-0 text-start text-white font-semibold"
    />
  )
  const finalContent = (
    <div className={'text-white'}>
      {filled} / {total}
    </div>
  )

  return (
    <div className={'flex flex-col gap-1'}>
      <div className={'text-sm'}>Order partially filled</div>
      <div className={'text-sm flex flex-row gap-1'}>
        <div>ID:</div>
        <NotificationId value={order.order_id} displayValue={slicedOrderID} />
      </div>
      <div className={'text-sm flex flex-row gap-1'}>
        <div>Total Filled:</div>
        <div>{finalContent}</div>
      </div>
    </div>
  )
}

export const getContentForType = (notificationType: NotificationType, params: Inexpressible) => {
  switch (notificationType) {
    case NotificationType.DISPOSABLE:
    case NotificationType.SIMPLE:
      return generateSimpleNotification(params)
    case NotificationType.TOKEN:
      return generateTokenNotification(params.token)
    case NotificationType.ROLES:
      return generateRolesNotification(params.roles, params.user)
    case NotificationType.DEPOSIT_WITHDRAW_BALANCE:
      return generateAddRemoveBalanceNotification(
        params.selectedAsset,
        params.text,
        params.amount,
        params.isAdding,
      )
    case NotificationType.ASSET_OPERATIONS:
      return generateAssetOperationsNotification(params.text, params.amount, params.asset)
    case NotificationType.CREATE_ASSET:
      return generateCreateAssetNotification(params.symbol)
    case NotificationType.CREATE_ORDER:
      return generateNewOrderNotification(
        params.orderID,
        params.selectedTradeType,
        params.selectedOrderSide,
        params.priceValue,
        params.leverageValue,
        params.baseAsset,
        params.quoteAsset,
        params.size,
        params.tickSize,
      )
    case NotificationType.CREATE_ORDER_BOOK:
      return generateCreateOrderBookNotification(params.baseAsset, params.quoteAsset)
    case NotificationType.ORDER_CANCEL:
      return generateOrderCancelNotification(params.orderID)
    case NotificationType.ADD_LIQUIDITY:
      return generateAddLiquidityNotification(
        params.orderBooks,
        params.allAssets,
        params.orderbookId,
        params.baseAssetShares,
        params.quoteAssetShares,
        params.poolShares,
      )
    case NotificationType.REMOVE_LIQUIDITY:
      return generateRemoveLiquidityNotification(
        params.orderBooks,
        params.allAssets,
        params.orderbookId,
        params.baseAssetShares,
        params.quoteAssetShares,
        params.shares,
      )
    case NotificationType.ORDER_EXECUTED_OR_CANCELED:
      return generateOrderStateChanged(params.order)
    case NotificationType.ORDER_LIQUIDATION:
      return generateOrderLiquidation(params.order, params.baseAsset)
    case NotificationType.ORDER_PARTIAL:
      return generateOrderStatePartial(params.order, params.baseAsset, params.quoteAsset)
    case NotificationType.REPAY:
      return generateRepayNotification(params.asset, params.amount)
    case NotificationType.POSITION_CLOSED:
      return generatePositionClosedNotification(params.realizedPnl, params.account, params.asset)
    default:
      return <div>Unknown Operation Type</div>
  }
}

export function createNotification(
  title: string,
  params: Inexpressible,
  notificationType = NotificationType.SIMPLE,
  notificationStatus = NotificationStatus.SUCCESS,
  skipStoring = false,
  toastOptions?: ExternalToast,
) {
  if (shouldSuppressOrderNotification(notificationType, notificationStatus)) {
    Logger.log('order notification suppressed by user settings', {
      notificationType,
      notificationStatus,
      title,
    })
    return
  }

  if (
    notificationType === NotificationType.ORDER_LIQUIDATION &&
    !areLiquidationNotificationsEnabled()
  ) {
    Logger.log('liquidation notification suppressed by user settings', { title })
    return
  }

  if (notificationType === NotificationType.ORDER_LIQUIDATION && areToastsEnabled()) {
    triggerLiquidationAlert(params.order)
  }

  if (notificationType === NotificationType.CREATE_ORDER) {
    playSound('createOrder')
  } else if (notificationType === NotificationType.ORDER_CANCEL) {
    playSound('cancelOrder')
  } else if (
    (notificationType === NotificationType.ORDER_EXECUTED_OR_CANCELED &&
      params.order.state === orderStatus.FILLED) ||
    notificationType === NotificationType.ORDER_PARTIAL
  ) {
    playSound('orderFilled')
  }

  Logger.log(
    'notificationType',
    notificationType,
    'notificationStatus',
    notificationStatus,
    'skipStoring',
    skipStoring,
    'title',
    title,
  )

  const notificationItem: NotificationItem = {
    id: Date.now().toString(36),
    title: title,
    params: params,
    notificationStatus: notificationStatus,
    notificationType: notificationType,
    timestamp: Date.now(),
  }

  if (areToastsEnabled() && notificationType !== NotificationType.ORDER_LIQUIDATION) {
    toast.custom(
      (id) => (
        <div className={'w-[min(92vw,420px)]'}>
          <NotificationItemComponent
            item={notificationItem}
            onNotificationClose={
              //dismiss toast
              () => {
                toast.dismiss(id)
              }
            }
          />
        </div>
      ),
      toastOptions,
    )
  }

  if (!skipStoring) {
    if (notificationType === NotificationType.ORDER_PARTIAL) {
      store.dispatch(updatePartialNotification(notificationItem))
    } else if (notificationType !== NotificationType.DISPOSABLE) {
      store.dispatch(addNotification(notificationItem))
    }
  }
}
